import { useCallback, useEffect, useState } from 'react'
import {
  getCurrentUser,
  fetchAuthSession,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  type SignInInput,
  type SignUpInput,
} from 'aws-amplify/auth'
import { useAuthStore } from '@/store/authStore'
import { useCreditStore } from '@/store/creditStore'
import { api } from '@/lib/axios'
import { initializeWelcomeBonusBackend } from '@/hooks/useCredits'

const DEV_SKIP_AUTH = import.meta.env.DEV && import.meta.env.VITE_SKIP_AUTH === 'true'

// Interface for backend user data
interface BackendUserData {
  email: string
  phoneNumber: string
  accountType: 'individual' | 'organization'
  pan: string
  aadhar?: string
  orgName?: string
  cin?: string
}

interface BackendUserResponse {
  id: string
  email: string
  accountType: 'individual' | 'organization'
  createdAt: string
  [key: string]: unknown
}

async function resolveIsAdmin(): Promise<boolean> {
  try {
    const session = await fetchAuthSession()
    const groups =
      (session.tokens?.accessToken.payload['cognito:groups'] as string[] | undefined) ?? []
    return groups.includes('admin')
  } catch {
    return false
  }
}

/**
 * Create user in backend database after Cognito signup
 * Should be called after successful Cognito email confirmation
 */
async function createBackendUser(userData: BackendUserData): Promise<BackendUserResponse> {
  try {
    const { data } = await api.post<BackendUserResponse>('/users', userData)
    return data
  } catch (err) {
    console.error('Failed to create backend user:', err)
    throw err
  }
}

/**
 * Get/verify user from backend database after login
 * Returns user data if found, throws error if not found
 */
async function getBackendUser(): Promise<BackendUserResponse> {
  try {
    const { data } = await api.get<BackendUserResponse>('/users/me')
    return data
  } catch (err) {
    console.error('User not found in backend:', err)
    throw err
  }
}

export function useAuth() {
  const { user, isAuthenticated, isAdmin, setUser, setIsAdmin, clearAuth } = useAuthStore()
  const [loading, setLoading] = useState(
    () => !(import.meta.env.DEV && import.meta.env.VITE_SKIP_AUTH === 'true'),
  )
  const [error, setError] = useState<string | null>(null)

  // Handle OAuth redirect errors on mount (separate effect)
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const errorParam = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')

    if (errorParam) {
      const errorMessage = errorDescription
        ? decodeURIComponent(errorDescription).replace(/\+/g, ' ')
        : errorParam
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError(errorMessage)
      // Clean up error from URL
      window.history.replaceState({}, document.title, window.location.pathname)
      setLoading(false)
    }
  }, []) // Run only once on mount

  // Hydrate user on mount
  useEffect(() => {
    const { initialize } = useCreditStore.getState()

    // In dev mode with VITE_SKIP_AUTH=true, skip Cognito and give welcome credits
    if (DEV_SKIP_AUTH) {
      setIsAdmin(import.meta.env.VITE_IS_ADMIN === 'true')
      initialize()
      return
    }

    getCurrentUser()
      .then(async (u) => {
        setUser(u)
        setIsAdmin(await resolveIsAdmin())

        // Verify user exists in backend database (handles both regular login and OAuth login)
        try {
          await getBackendUser()
        } catch (backendErr) {
          console.warn('Backend user verification failed:', backendErr)
          // User exists in Cognito but not in backend - this could be a new OAuth user
          // Log warning but continue - backend user should be created on first OAuth login
        }

        initialize()
        // Sync welcome bonus to backend (idempotent)
        await initializeWelcomeBonusBackend()
      })
      .catch(() => clearAuth())
      .finally(() => setLoading(false))
  }, [setUser, setIsAdmin, clearAuth])

  const login = useCallback(
    async (input: SignInInput) => {
      setError(null)
      if (DEV_SKIP_AUTH) {
        setUser({ userId: 'dev-user', username: input.username } as Parameters<typeof setUser>[0])
        setIsAdmin(import.meta.env.VITE_IS_ADMIN === 'true')
        useCreditStore.getState().initialize()
        return
      }
      try {
        await signIn(input)
        const currentUser = await getCurrentUser()
        setUser(currentUser)
        setIsAdmin(await resolveIsAdmin())

        // Verify user exists in backend database
        try {
          await getBackendUser()
        } catch (backendErr) {
          console.warn('Backend user verification failed:', backendErr)
          // User exists in Cognito but not in backend - this could be a new user
          // or a sync issue. Log warning but allow login to proceed
        }

        useCreditStore.getState().initialize()
        // Sync welcome bonus to backend (idempotent)
        await initializeWelcomeBonusBackend()
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Sign-in failed'
        setError(message)
        throw err
      }
    },
    [setUser, setIsAdmin],
  )

  const register = useCallback(async (input: SignUpInput) => {
    setError(null)
    if (DEV_SKIP_AUTH) {
      return {
        isSignUpComplete: false,
        userId: 'dev-user',
        nextStep: { signUpStep: 'CONFIRM_SIGN_UP' },
      } as Awaited<ReturnType<typeof signUp>>
    }
    try {
      return await signUp(input)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-up failed'
      setError(message)
      throw err
    }
  }, [])

  const confirm = useCallback(async (username: string, code: string) => {
    setError(null)
    if (DEV_SKIP_AUTH) {
      return
    }
    try {
      await confirmSignUp({ username, confirmationCode: code })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Confirmation failed'
      setError(message)
      throw err
    }
  }, [])

  const forgotPassword = useCallback(async (username: string) => {
    setError(null)
    try {
      return await resetPassword({ username })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Password reset failed'
      setError(message)
      throw err
    }
  }, [])

  const confirmForgotPassword = useCallback(
    async (username: string, code: string, newPassword: string) => {
      setError(null)
      try {
        await confirmResetPassword({ username, confirmationCode: code, newPassword })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Password reset confirmation failed'
        setError(message)
        throw err
      }
    },
    [],
  )

  const loginWithGoogle = useCallback(async () => {
    setError(null)
    try {
      await signInWithRedirect({ provider: 'Google' })
      // Note: After redirect back, useEffect will handle backend user verification
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google sign-in failed'
      setError(message)
      throw err
    }
  }, [])

  const logout = useCallback(async () => {
    await signOut()
    clearAuth()
  }, [clearAuth])

  return {
    user,
    isAuthenticated,
    isAdmin,
    loading,
    error,
    login,
    loginWithGoogle,
    register,
    confirm,
    forgotPassword,
    confirmForgotPassword,
    logout,
    createBackendUser,
  }
}
